import React from 'react'
import PropTypes from 'prop-types'

import { normalize, search, listVideos, listEvents } from './api'
import VideoSelectorCompact from './VideoSelectorCompact'
import VideoSelectorExpanded from './VideoSelectorExpanded'

import './style.scss'
import { showNotice } from '../../../Layout/Notices/Notices'

class VideoSelector extends React.Component {
  constructor(props) {
    super(props)

    let selection = []
    if (typeof props.selection === 'string' && props.selection.length > 0) {
      selection = props.selection.split(' ').map((keyStr) => ({ key: keyStr }))
    } else if (typeof props.selection === 'object') {
      selection = props.selection.map((keyStr) => ({ key: keyStr }))
    }

    this.state = {
      inited: false,
      results: [],
      totalResults: 0,
      selection,
      selectionCount: 0,
      loading: true,
    }

    this.lastSearchXhr = {}
    this.searchTimeoutId = 0

    this.getPlaceholder = this.getPlaceholder.bind(this)
    this.getSelectionKeys = this.getSelectionKeys.bind(this)
    this.performSearch = this.performSearch.bind(this)
    this.normalizeSelection = this.normalizeSelection.bind(this)
    this.normalizeResults = this.normalizeResults.bind(this)
    this.toggleItem = this.toggleItem.bind(this)
    this.toggleResults = this.toggleResults.bind(this)
  }

  componentDidMount() {
    this.cancelRemainingRequests = false

    this.makeCancellableRequest(
      normalize,
      this.getSelectionKeys().join(' '),
      (data) => {
        const { selectionLimit } = this.props
        const selection = data.results.filter(
          (obj) => parseInt(obj.selected_p, 10) === 1
        )

        const s = selection.slice(0, selectionLimit)

        this.setState({
          selection: s,
          selectionCount: s.length,
          inited: true,
        })
      }
    )
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const { selection } = this.state
    if (nextProps.resetSelection && selection.length !== 0) {
      this.setState({
        selection: [],
        results: [],
        selectionCount: 0,
      })
    }
  }

  componentWillUnmount() {
    this.cancelRemainingRequests = true
  }

  getPlaceholder() {
    const objectArray = []

    const allVideos = this.getSelectionKeys().indexOf('site:photos') > -1
    if (allVideos) {
      objectArray.push('all videos')
    }

    const allLive = this.getSelectionKeys().indexOf('site:live') > -1
    if (allLive) {
      objectArray.push('all live events')
    } else {
      const events = this.getSelectionKeys().filter((keyStr) =>
        /^live:/.test(keyStr)
      )
      if (events.length === 1) {
        objectArray.push('1 live event')
      } else if (events.length > 1) {
        objectArray.push(`${events.length} live events`)
      }
    }

    if (!allVideos) {
      const albums = this.getSelectionKeys().filter((keyStr) =>
        /^album:/.test(keyStr)
      )
      if (albums.length === 1) {
        objectArray.push('1 category')
      } else if (albums.length > 1) {
        objectArray.push(`${albums.length} categories`)
      }

      const photos = this.getSelectionKeys().filter((keyStr) =>
        /^photo:/.test(keyStr)
      )
      if (photos.length === 1) {
        objectArray.push('1 video')
      } else if (photos.length > 1) {
        objectArray.push(`${photos.length} videos`)
      }
    }

    if (objectArray.length > 0) {
      let selectionStr = `${objectArray.join(', ')} selected`
      selectionStr =
        selectionStr.charAt(0).toUpperCase() + selectionStr.slice(1)
      return selectionStr
    }

    return 'Nothing is selected'
  }

  getSelectionKeys(selectionParam) {
    let newSelection = selectionParam

    if (newSelection === undefined) {
      const { selection } = this.state
      newSelection = selection
    }

    return newSelection.map((obj) => obj.key)
  }

  makeCancellableRequest(apiFunc, data, callback, errorText) {
    let isAborted = false
    const errorCallback = () => {
      showNotice({
        message: errorText || 'Error occurred while loading data.',
        expirationMillis: 5e3,
        isClosable: true,
      })
    }

    apiFunc(
      data,
      (...args) => {
        if (!this.cancelRemainingRequests && !isAborted) {
          callback(...args)
        }
      },
      errorCallback
    )

    return {
      abort() {
        isAborted = true
      },
    }
  }

  performSearch(s, d = null, searchMode = 'site') {
    this.setState({ loading: true })

    // Abort any ongoing requests
    if (this.lastSearchXhr && this.lastSearchXhr.abort) {
      this.lastSearchXhr.abort()
    }
    // Clear timeouts for requests waiting to be sent
    window.clearTimeout(this.searchTimeoutId)

    if (searchMode === 'site') {
      const { searchIn } = this.props

      const requestData = {
        search: s,
        selection: this.getSelectionKeys().join(' '),
        search_in: searchIn.join(' '),
        size: 10,
        ...d,
      }

      this.searchTimeoutId = window.setTimeout(() => {
        this.lastSearchXhr = this.makeCancellableRequest(
          search,
          requestData,
          (data) => {
            this.setState({
              results: data.results,
              loading: false,
            })
          }
        )
      }, 300)
    } else if (searchMode === 'live') {
      const requestData = { search: s, ...d }

      this.searchTimeoutId = window.setTimeout(() => {
        this.lastSearchXhr = this.makeCancellableRequest(
          listEvents,
          requestData,
          (data) => {
            const { results: stateResults } = this.state
            const shouldConcat = requestData.p && requestData.p > 1
            const results = shouldConcat
              ? stateResults.concat(data.live)
              : data.live

            this.setState({
              results: this.normalizeResults(results),
              totalResults: parseInt(data.total_count, 10),
              loading: false,
            })
          }
        )
      })
    } else {
      const requestData = { search: s, video_encoded_p: 1, ...d }
      const successCallback = (data) => {
        const { results: stateResults } = this.state
        const shouldConcat = requestData.p && requestData.p > 1
        const results = shouldConcat
          ? stateResults.concat(data.photos)
          : data.photos

        this.setState({
          results: this.normalizeResults(results),
          totalResults: parseInt(data.total_count, 10),
          loading: false,
        })
      }
      const errorText = 'The video list could not be loaded.'

      this.searchTimeoutId = window.setTimeout(() => {
        this.lastSearchXhr = this.makeCancellableRequest(
          listVideos,
          requestData,
          successCallback,
          errorText
        )
      }, 300)
    }
  }

  normalizeResults(results, selectionParam) {
    const { selection } = this.state
    const newSelection = selectionParam ?? selection

    const newResults = results.map((result) => {
      const newResult = result
      if (result.photo_id) {
        newResult.key = `photo:${result.photo_id}`
        newResult.object_type = 'photo'
      } else if (result.live_id) {
        newResult.key = `live:${result.live_id}`
        newResult.object_type = 'live'
      } else if (result.album_id) {
        newResult.key = `album:${result.album_id}`
        newResult.object_type = 'album'
      }
      const index = newSelection.findIndex((obj) => obj.key === newResult.key)
      newResult.selected_p = index > -1 ? 1 : 0
      return newResult
    })
    return newResults
  }

  normalizeSelection() {
    window.setTimeout(() => {
      this.lastSearchXhr = this.makeCancellableRequest(
        search,
        {
          search: '',
          selection: this.getSelectionKeys().join(' '),
          search_in: 'photos albums live site',
        },
        (data) => {
          this.setState({
            results: data.results,
          })
        }
      )
    }, 300)
  }

  toggleItem(result) {
    let newSelection
    const newResult = { ...result }

    const { selectionLimit, onChange } = this.props
    const { selection } = this.state

    const index = selection.findIndex((obj) => obj.key === newResult.key)

    if (index > -1) {
      newSelection = [].concat(selection)
      newSelection.splice(index, 1)
      newResult.selected_p = 0
    } else {
      if (selection.length >= selectionLimit) {
        showNotice({
          message: `You reached the maximum limit of ${selectionLimit} ${
            selectionLimit === 1 ? 'item' : 'items'
          }. Deselect some to select new ones.`,
          expirationMillis: 5e3,
          isClosable: true,
        })

        return
      }

      // Making sure that there can only be same type of objects in the selection
      // If the object type or the result is `album` we always clear the selection
      // since we are not intereseted in mixing albums and photos
      const coercedSelection =
        selection.some((x) => x.object_type === 'album') ||
        newResult.object_type === 'album'
          ? []
          : selection

      newSelection = coercedSelection.concat([newResult])
      newResult.selected_p = 1
    }
    // to be refactored
    // eslint-disable-next-line
    const results = this.normalizeResults(this.state.results, newSelection)
    this.setState({
      selection: newSelection,
      results,
      selectionCount: newSelection.length,
    })
    this.makeCancellableRequest(
      normalize,
      this.getSelectionKeys(newSelection).join(' '),
      () => {}
    )

    onChange(
      this.getSelectionKeys(newSelection).join(' '),
      newSelection,
      this.getSelectionKeys(newSelection)
    )
  }

  toggleResults() {
    // the code should be refactored
    const { results, selection } = this.state
    const { selectionLimit } = this.props

    let newSelection = [].concat(selection)

    const unselectedResults = results.filter(
      (result) => result.selected_p === 0
    )

    if (unselectedResults.length > 0) {
      // Making sure that we can't go over the selection limit
      if (
        newSelection.filter((result) => result.selected_p === 1).length >=
        selectionLimit
      ) {
        showNotice({
          message: `You reached the maximum limit of ${selectionLimit} items.
            Deselect some to select new ones.`,
          expirationMillis: 5e3,
          isClosable: true,
        })
        return
      }

      const s = []
      for (
        let i = 0;
        i < selectionLimit - selection.length && i < unselectedResults.length;
        i += 1
      ) {
        unselectedResults[i].selected_p = 1 // Because...shallow copy
        s.push({ ...unselectedResults[i], selected_p: 1 })
      }

      newSelection = [...selection, ...s]
    } else {
      // All results already selected - deselect them all
      results.forEach((result) => {
        const index = newSelection.findIndex((obj) => obj.key === result.key)
        if (index > -1) {
          newSelection.splice(index, 1)
          // TODO (noahlaux): refactor
          // eslint-disable-next-line
          result.selected_p = 0
        }
      })
    }
    this.setState({
      selection: newSelection,
      selectionCount: newSelection.length,
    })
    this.makeCancellableRequest(
      normalize,
      this.getSelectionKeys(newSelection).join(' '),
      () => {}
    )

    const { onChange } = this.props

    onChange(this.getSelectionKeys(newSelection).join(' '), newSelection)
  }

  render() {
    const {
      inited,
      results,
      totalResults,
      selection,
      selectionCount,
      loading,
    } = this.state
    const { expanded, theme, searchIn, selectionLimit } = this.props

    if (!inited) {
      return null
    }

    if (!expanded) {
      return (
        <VideoSelectorCompact
          theme={theme}
          performSearch={this.performSearch}
          results={results}
          selectionCount={selectionCount}
          toggleItem={this.toggleItem}
          getPlaceholder={this.getPlaceholder}
          /* eslint-disable-next-line  */
          setSelectionToResults={() => this.setState({ results: selection })}
        />
      )
    }

    return (
      <VideoSelectorExpanded
        searchIn={searchIn}
        performSearch={this.performSearch}
        results={results}
        totalResults={totalResults}
        selection={selection}
        selectionCount={selectionCount}
        toggleItem={this.toggleItem}
        toggleResults={this.toggleResults}
        getPlaceholder={this.getPlaceholder}
        loading={loading}
        selectionLimit={selectionLimit}
      />
    )
  }
}

VideoSelector.propTypes = {
  selection: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
  ]).isRequired,
  searchIn: PropTypes.arrayOf(
    PropTypes.oneOf(['photos', 'albums', 'live', 'site'])
  ),
  onChange: PropTypes.func,
  expanded: PropTypes.bool,
  resetSelection: PropTypes.bool,
  theme: PropTypes.oneOf(['light', 'dark']),
  selectionLimit: PropTypes.number,
}

VideoSelector.defaultProps = {
  searchIn: ['photos', 'albums', 'live', 'site'],
  onChange: () => {},
  expanded: false,
  resetSelection: false,
  theme: 'light',
  selectionLimit: Infinity,
}

export default VideoSelector
